import { datadogRum } from '@datadog/browser-rum'
import type { RumInitConfiguration } from '@datadog/browser-rum'
import { getEnvName } from '@disco/constants/environment'

export type RumOverrides = Pick<RumInitConfiguration, 'sessionSampleRate'>

/**
 * Returns the allowed tracing URLs for distributed tracing.
 * These URLs will have trace context headers (x-datadog-trace-id, etc.) injected
 * to connect frontend RUM traces with backend APM traces.
 */
function getAllowedTracingUrls(): Array<string | RegExp> {
  const envName = getEnvName()

  // Always trace same-origin requests (relative URLs like /api/...)
  const urls: Array<string | RegExp> = []

  if (typeof window !== 'undefined' && window.location?.origin) {
    // Trace requests to the same origin
    urls.push(window.location.origin)
  }

  // Add environment-specific API domains
  if (envName === 'prod') {
    urls.push(/https:\/\/[^.]*\.disco\.ac/)
  } else if (envName === 'staging') {
    urls.push(/https:\/\/[^.]*\.disco-staging-2\.com/)
  }

  return urls
}

/**
 * Initializes Datadog Real User Monitoring (RUM).
 *
 * Configures Datadog RUM with environment-specific settings for monitoring
 * frontend performance and user interactions. Uses different sampling rates
 * for production vs development environments.
 *
 * Distributed tracing is enabled via allowedTracingUrls to connect frontend
 * RUM traces with backend APM traces for end-to-end request visibility.
 *
 * @example
 * ```typescript
 * // Call once during app initialization
 * initDatadog()
 * ```
 */
export function initDatadog(options?: RumOverrides) {
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_TOKEN,
    site: 'datadoghq.com',
    service: DATADOG_SERVICE_NAME,
    env: getEnvName(),
    version: COMMITHASH,
    sessionSampleRate: getEnvName() === 'prod' ? 2 : 1,
    sessionReplaySampleRate: 10, // sessionReplaySampleRate is a percentage of sessionSampleRate
    defaultPrivacyLevel: 'mask',
    // Distributed tracing configuration to connect FE RUM with BE APM
    allowedTracingUrls: getAllowedTracingUrls(),
    traceSampleRate: 20,
    ...options,
  })
}

/**
 * Sets or clears the user ID for Datadog tracking.
 *
 * Associates user sessions with a specific user ID for better tracking
 * and debugging. Only stores the user ID (no personal information)
 * for GDPR compliance. Handles errors gracefully.
 *
 * @param id - User ID to associate with the session, or null to clear
 *
 * @example
 * ```typescript
 * // Set user when they log in
 * setDatadogUser(12345)
 *
 * // Clear user when they log out
 * setDatadogUser(null)
 * ```
 */
export function setDatadogUser(id: number | null) {
  try {
    if (id) {
      datadogRum.setUser({
        id: id.toString(),
      })
    } else {
      datadogRum.clearUser()
    }
  } catch (error: any) {
    console.log(error)
  }
}
